























































import type { PropType } from '@nuxtjs/composition-api';
import { onMounted } from '@nuxtjs/composition-api';
import {
  computed,
  defineComponent,
  ref,
  toRef,
  useContext,
  useRouter
} from '@nuxtjs/composition-api';
import { SfButton } from '@storefront-ui/vue';

import {
  useProduct,
  useUiNotification,
  useUiState,
  useUser
} from '~/composables';
import { useBooxi, useLink, useScreenSize } from '~/diptyqueTheme/composable';
import {
  generateGoogleCalendarLink,
  generateICSFile
} from '~/diptyqueTheme/helpers/generateCalendarInvitation';
import type {
  ButtonIconPosition,
  ButtonLinkTarget,
  ButtonModelTarget,
  ButtonVariant
} from '~/diptyqueTheme/types/contentful';
import {
  ButtonIconPositionEnum,
  ButtonTargetEnum,
  ButtonVariantEnum
} from '~/diptyqueTheme/types/contentful';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { Logger } from '~/helpers/logger';
import type { Product } from '~/modules/catalog/product/types';

export default defineComponent({
  name: 'VaimoButton',
  components: {
    SfButton,
    VaimoNewsletterSubscription: () =>
      import('organisms/VaimoNewsletterSubscription.vue')
  },
  props: {
    variant: {
      required: false,
      type: String as PropType<ButtonVariant>,
      default: ButtonVariantEnum.Primary,
      validator: (value: string) =>
        [
          'primary',
          'default',
          'external',
          'link',
          'circle',
          'small',
          'link with arrow',
          'link with dots',
          'button with dots',
          'button with dots white',
          'button with dots black',
          'link grey',
          'edito link',
          'email',
          'underlined',
          'quick buy',
          'quick buy white',
          'quick buy black',
          'add to cart',
          'label dark large'
        ].includes(value)
    },
    type: {
      required: false,
      type: String,
      default: 'button'
    },
    isEmail: {
      type: Boolean,
      required: false,
      default: false
    },
    iconPosition: {
      required: false,
      type: String as PropType<ButtonIconPosition>,
      default: ButtonIconPositionEnum.Right,
      validator: (value: string) => ['right', 'left'].includes(value)
    },
    labelCentered: {
      required: false,
      type: Boolean,
      default: false
    },
    fullWidth: {
      required: false,
      type: Boolean,
      default: false
    },
    link: {
      required: false,
      type: String,
      default: null
    },
    emailButtonText: {
      type: String,
      default: ''
    },
    icon: {
      required: false,
      type: String,
      default: ''
    },
    linkBehavior: {
      required: false,
      type: String as PropType<ButtonModelTarget>,
      default: ButtonTargetEnum.Self,
      validator: (value: string) =>
        ['open in current tab', 'open in new tab'].includes(value)
    },
    preventDefault: Boolean,
    disabled: Boolean,
    loading: Boolean,
    block: Boolean,
    // eslint-disable-next-line vue/require-default-prop
    buttonText: String,
    desktopType: {
      required: false,
      type: String,
      default: ''
    },
    buttonVisibility: {
      required: false,
      type: Boolean,
      default: true
    },
    layerContent: {
      required: false,
      type: [Object, null],
      default: null
    },
    additionalData: {
      required: false,
      type: [String, null],
      default: null
    },
    calendarData: {
      type: Object,
      required: false,
      default: () => ({})
    },
    isButtonVisible: {
      type: Boolean,
      required: false,
      default: true
    },
    isBooxiButton: Boolean,
    isAddToCartButton: Boolean,
    isCalendarButton: Boolean,
    forceShowSubmit: Boolean,
    countdownEmail: Boolean,
    buttonToShowAccountCreationForm: Boolean,
    gtmAttributes: {
      type: [Object, null],
      required: false,
      default: null
    },
    darkMode: {
      type: Boolean,
      default: false
    },
    hasDots: {
      type: Boolean,
      default: false
    }
  },
  emits: ['show-registration-form', 'add-to-cart', 'event-click'],
  setup(props, { emit }) {
    const { normalizeLink } = useLink();
    const { openContentLayer } = useUiState();
    const { i18n, app } = useContext();
    const { triggerBooxiModal } = useBooxi();
    const locale = i18n.localeProperties?.code;
    const { isAndroid } = app.$device;
    const { isDesktop } = useScreenSize();
    const { isAuthenticated } = useUser();
    const { loadBooxiScript } = useBooxi();

    const _disabled = ref(false);
    const isDisabled = computed(() => props.disabled || _disabled.value);
    const isPreventedDefault = toRef(props, 'preventDefault');
    const isActive = ref(false);

    const variants = [
      'primary',
      'default',
      'external',
      'link',
      'circle',
      'small',
      'link with arrow',
      'link with dots',
      'button with dots',
      'button with dots white',
      'button with dots black',
      'link grey',
      'edito link',
      'email',
      'underlined',
      'quick buy',
      'quick buy white',
      'quick buy black',
      'add to cart',
      'label dark large'
    ];

    function generateButtonClass(type) {
      const prefix = 'vaimo-button__';
      const formattedClass = variants.includes(type)
        ? type.replace(/ /g, '-')
        : 'default';

      return {
        [`${prefix}${formattedClass}`]: true,
        is_mobile: !isDesktop.value
      };
    }

    const getButtonClasses = computed(() => {
      const baseClasses = {
        'vaimo-button__block': props.block,
        'vaimo-button__disabled': isDisabled.value,
        'w-full': props.fullWidth,
        'vaimo-button__show-registration-form':
          props.buttonToShowAccountCreationForm,
        'vaimo-button--dark': props.darkMode,
        'vaimo-button--dots': props.hasDots
      };
      const mobileClasses =
        !isDesktop?.value || (isDesktop?.value && !props.desktopType)
          ? generateButtonClass(props.variant)
          : {};

      const desktopClasses =
        isDesktop?.value &&
        props.desktopType &&
        props.desktopType !== props.variant
          ? generateButtonClass(props.desktopType)
          : {};

      const fallbackClasses =
        !Object.keys(mobileClasses).length &&
        !Object.keys(desktopClasses).length
          ? generateButtonClass(props.variant)
          : {};

      const activeClass = {
        'vaimo-button__active': isActive.value
      };

      return {
        ...baseClasses,
        ...mobileClasses,
        ...desktopClasses,
        ...fallbackClasses,
        ...activeClass
      };
    });

    const getWrapperClass = computed(() => ({
      'icon-right': props.iconPosition === ButtonIconPositionEnum.Right,
      'icon-left': props.iconPosition === ButtonIconPositionEnum.Left,
      center: props.labelCentered,
      'justify-between': !!props.additionalData
    }));

    const getLinkTarget = computed<ButtonLinkTarget>(() => {
      const targetMap = {
        [ButtonTargetEnum.Self]: '_self',
        [ButtonTargetEnum.Blank]: '_blank'
      };

      return targetMap[props.linkBehavior] || '_self';
    });

    const getComputedLink = computed(() => {
      if (
        isPreventedDefault.value ||
        isDisabled.value ||
        props.isBooxiButton ||
        props.isCalendarButton ||
        props.buttonToShowAccountCreationForm ||
        props.link === '#'
      )
        return '';
      if (props.layerContent || props.isAddToCartButton || addToCartSku.value)
        return ''; // Prevent go to next page if Layer is configured or addToCartSku is set

      if (props.link?.includes('mailto:')) {
        const mailtoIndex = props.link?.toLowerCase().indexOf('mailto:');

        return mailtoIndex === -1
          ? normalizeLink(props.link)
          : props.link?.slice(mailtoIndex).trim();
      }

      if (props.link?.includes(process.env.VSF_STORE_URL)) {
        return props.link?.replace(process.env.VSF_STORE_URL, '');
      }

      return normalizeLink(props.link);
    });

    const addToCartSku = computed(() => {
      const delimiter = '#addToCart:';
      if (!props.link?.includes(delimiter)) {
        return null;
      }

      const parts = props.link.split(delimiter);
      const minParts = 2;
      if (parts.length < minParts || !parts[1]) {
        return null;
      }

      const extractedSku = parts[1].trim();
      return extractedSku || null;
    });

    const onClick = async () => {
      emit('event-click');

      if (!getComputedLink.value) {
        if (props.isAddToCartButton) {
          emit('add-to-cart');
        } else if (addToCartSku.value) {
          await addToCartFromButton(addToCartSku.value);
        } else if (props.layerContent) {
          const { heading, content } = props.layerContent;
          openContentLayer(heading, content);
        } else if (props.isBooxiButton) {
          triggerBooxiModal();
        } else if (props.isCalendarButton) {
          const event = {
            end: props.calendarData.endDate,
            title: props.calendarData.title,
            description: props.calendarData.description
          };
          await generateICSFile(event);
          if (isAndroid) {
            generateGoogleCalendarLink(event);
          }
        } else if (props.buttonToShowAccountCreationForm) {
          emit('show-registration-form');
          isActive.value = !isActive.value;
        }
      }
      if (props.gtmAttributes) {
        app.$gtm.push(props.gtmAttributes);
      }
    };

    // hotfix for DPTQ-6056. Add to cart should be handled on the top level component.
    const { addItemToCart } = useAddToCart();
    const { getProductDetailsToAddToCart } = useProduct();
    const { send: sendNotification } = useUiNotification();
    const router = useRouter();
    const addToCartFromButton = async (sku: string) => {
      _disabled.value = true;
      try {
        const result = await getProductDetailsToAddToCart({
          filter: {
            sku: {
              eq: sku
            }
          }
        });

        if (result?.items?.[0]) {
          await addItemToCart({
            product: result.items[0] as Product,
            quantity: 1
          });
        } else {
          sendNotification({
            id: Symbol('button-add-to-cart-error'),
            message: i18n.t('This product is currently unavailable') as string,
            persist: false,
            type: 'danger',
            area: 'top'
          });
        }
      } catch (error) {
        router.push({ name: 'error' });
        Logger.error('[VaimoButton] addToCartFromButton error:', error);
      } finally {
        _disabled.value = false;
      }
    };

    onMounted(() => {
      if (props.isBooxiButton) {
        loadBooxiScript();
      }
    });

    return {
      getLinkTarget,
      getComputedLink,
      getWrapperClass,
      getButtonClasses,
      onClick,
      locale,
      isAuthenticated,
      isDisabled
    };
  }
});
